<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="950px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Work Session Type"
      text="Edit Work Session Type"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row>
            <v-col cols="7">
              <v-text-field
                v-model="localWorkSessionType.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"
                label="Name"
                outlined
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="localWorkSessionType.code"
                label="Shift Code"
                outlined
              />
            </v-col>
            <v-col cols="2">
              <v-checkbox
                v-model="localWorkSessionType.is_active"
              >
                <template v-slot:label>
                  <div>
                    Active?
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row
            v-for="(workSession, i) in localWorkSessionType.work_sessions"
            :key="i"
          >
            <v-col class="py-0">
              <v-text-field
                :label="getDay(workSession.day)"
                disabled
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="workSession.start_time"
                v-validate="'required|time_before_or_equal:' + workSession.end_time"
                :error-messages="errors.collect('startTimeDisplay' + i)"
                :name="'startTimeDisplay' + i"
                :data-vv-as="getDay(workSession.day) + ' start time'"
                outlined
                label="From"
                type="time"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="workSession.end_time"
                v-validate="'required|time_after_or_equal:' + workSession.start_time"
                :error-messages="errors.collect('endTimeDisplay' + i)"
                :name="'endTimeDisplay' + i"
                :data-vv-as="getDay(workSession.day) + ' end time'"
                outlined
                label="To"
                type="time"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="workSession.lunch_range_from"
                v-validate="'time_before_or_equal:' + workSession.lunch_range_to"
                :error-messages="errors.collect('lunchFromDisplay' + i)"
                :name="'lunchFromDisplay' + i"
                :data-vv-as="getDay(workSession.day) + ' lunch start time'"
                outlined
                label="Lunch Range From"
                type="time"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="workSession.lunch_range_to"
                v-validate="'time_after_or_equal:' + workSession.lunch_range_from"
                :error-messages="errors.collect('lunchToDisplay' + i)"
                :name="'lunchToDisplay' + i"
                :data-vv-as="getDay(workSession.day) + ' lunch end time'"
                outlined
                label="Lunch Range To"
                type="time"
              />
            </v-col>
          </v-row>
        </v-container> 
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"   
          class="px-10 white--text"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      workSessionType: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localWorkSessionType: null,
        rules: [
          value => !!value || 'Required.',
        ],
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localWorkSessionType = { ...this.workSessionType }

        function toMinutes (time) {
          if (!time) return null
          const [h, m] = time.split(':')
          return (parseInt(h) * 60) + parseInt(m)
        }

        this.$validator.extend('time_before_or_equal', {
          getMessage (field, args) {
            return `The ${field} must be earlier than end time.`
          },
          validate (value, args) {
            const [otherValue] = args
            return toMinutes(value) <= toMinutes(otherValue)
          },
        })

        this.$validator.extend('time_after_or_equal', {
          getMessage (field, args) {
            return `The ${field} must be later than start time.`
          },
          validate (value, args) {
            const [otherValue] = args
            return toMinutes(value) >= toMinutes(otherValue)
          },
        })
      },
    },
    methods: { 
      getDay (day) {
        switch (day) {
          case 1:
            return 'Monday'
          case 2:
            return 'Tuesday'
          case 3:
            return 'Wednesday'
          case 4:
            return 'Thursday'
          case 5:
            return 'Friday'
          case 6:
            return 'Saturday'
          default:
            return 'Sunday'
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
            this.$api.workSessionTypes().update(this.localWorkSessionType.id, this.prepareFormData())
              .then(res => {
                this.$emit('updated')
                this.$snack({ message: 'Work session type updated succesfully' })
              })
              .catch(() => {
                this.$snack({ 
                  color: 'error',
                  message: 'Failed to update work session type', 
                }) 
              })
              .then(() => {
                this.progressing = false
                this.dialog = false
              })
          })
      },
      prepareFormData () {
        return {
          name: this.localWorkSessionType.name,
          is_active: this.localWorkSessionType.is_active,
          code: this.localWorkSessionType.code,
          work_sessions: this.localWorkSessionType.work_sessions,
        }
      },
    },
  }
</script>
